/**
 * Sum unique minutes from payroll spans for a single day.
 * Each span should be confined within a single civil day and have start_iso/end_iso.
 * Returns { minutes, hours }.
 */
export function sumUniqueMinutesFromSpans(daySpans = []) {
  // Build [start, end) intervals as epoch ms; ignore invalid/zero-length
  const intervals = daySpans
    .map(s => {
      const a = Date.parse(s.start_iso || "");
      const b = Date.parse(s.end_iso   || "");
      return Number.isFinite(a) && Number.isFinite(b) && b > a ? [a, b] : null;
    })
    .filter(Boolean)
    .sort((i1, i2) => i1[0] - i2[0]); // sort by start

  if (intervals.length === 0) return { minutes: 0, hours: 0 };

  // Merge overlaps
  let mergedStart = intervals[0][0];
  let mergedEnd   = intervals[0][1];
  let totalMs = 0;

  for (let i = 1; i < intervals.length; i++) {
    const [s, e] = intervals[i];
    if (s <= mergedEnd) {
      // overlaps or touches; extend
      if (e > mergedEnd) mergedEnd = e;
    } else {
      // disjoint; close previous and start new
      totalMs += (mergedEnd - mergedStart);
      mergedStart = s;
      mergedEnd = e;
    }
  }
  // close last
  totalMs += (mergedEnd - mergedStart);

  const minutes = Math.round(totalMs / 60000);
  const hours = +(minutes / 60).toFixed(2);
  return { minutes, hours };
}

/**
 * Calculate night differential hours from unique (non-overlapping) time spans for a specific date.
 * This ensures that overlapping job times are only counted once.
 * 
 * @param {Array} daySpans - Array of span objects for a single day with start_iso/end_iso
 * @param {string} nightStart - Night start time in HH:MM format (e.g., "22:00")
 * @param {string} nightEnd - Night end time in HH:MM format (e.g., "06:00")
 * @returns {number} - Night differential hours based on unique time (rounded to 2 decimals)
 */
export function calculateNightDifferentialFromSpans(
  daySpans = [],
  nightStart = "22:00",
  nightEnd = "06:00"
) {
  if (!daySpans || daySpans.length === 0) return 0;

  // Get a reference date from the first span
  const referenceDate =
    daySpans[0]?.payroll_date || daySpans[0]?.start_iso?.split("T")[0];
  if (!referenceDate) return 0;

  // Parse night start and end times
  const [nightStartHour, nightStartMin] = nightStart.split(":").map(Number);
  const [nightEndHour, nightEndMin] = nightEnd.split(":").map(Number);

  // Create night period intervals
  const nightIntervals = [];

  const isCrossMidnight =
    nightEndHour < nightStartHour ||
    (nightEndHour === nightStartHour && nightEndMin < nightStartMin);

  if (isCrossMidnight) {
    // Night wraps midnight (e.g. 22:00–06:00).
    // For THIS civil date, night = 00:00–nightEnd AND nightStart–24:00.
    const earlyStart = new Date(`${referenceDate}T00:00:00`);
    const earlyEnd = new Date(`${referenceDate}T${nightEnd}:00`);
    nightIntervals.push([earlyStart.getTime(), earlyEnd.getTime()]);

    const lateStart = new Date(`${referenceDate}T${nightStart}:00`);
    const lateEnd = new Date(`${referenceDate}T23:59:59.999`);
    nightIntervals.push([lateStart.getTime(), lateEnd.getTime()]);
  } else {
    // Night period is within the same day
    const nightStartDate = new Date(`${referenceDate}T${nightStart}:00`);
    const nightEndDate = new Date(`${referenceDate}T${nightEnd}:00`);
    nightIntervals.push([nightStartDate.getTime(), nightEndDate.getTime()]);
  }

  // Build job intervals from spans
  const jobIntervals = daySpans
    .map((s) => {
      const a = Date.parse(s.start_iso || "");
      const b = Date.parse(s.end_iso || "");
      return Number.isFinite(a) && Number.isFinite(b) && b > a ? [a, b] : null;
    })
    .filter(Boolean)
    .sort((i1, i2) => i1[0] - i2[0]);

  if (jobIntervals.length === 0) return 0;

  // Merge overlapping job intervals to get unique time
  const mergedJobIntervals = [];
  let mergedStart = jobIntervals[0][0];
  let mergedEnd = jobIntervals[0][1];

  for (let i = 1; i < jobIntervals.length; i++) {
    const [s, e] = jobIntervals[i];
    if (s <= mergedEnd) {
      if (e > mergedEnd) mergedEnd = e;
    } else {
      mergedJobIntervals.push([mergedStart, mergedEnd]);
      mergedStart = s;
      mergedEnd = e;
    }
  }
  mergedJobIntervals.push([mergedStart, mergedEnd]);

  // Calculate overlap between merged job intervals and night intervals
  let totalNightMs = 0;
  for (const [jobStart, jobEnd] of mergedJobIntervals) {
    for (const [nStart, nEnd] of nightIntervals) {
      const overlapStart = Math.max(jobStart, nStart);
      const overlapEnd = Math.min(jobEnd, nEnd);
      if (overlapEnd > overlapStart) {
        totalNightMs += overlapEnd - overlapStart;
      }
    }
  }

  const nightMinutes = Math.round(totalNightMs / 60000);
  return +(nightMinutes / 60).toFixed(2);
}

/**
 * Calculate night differential hours for a job order based on night time settings.
 * Night time can span across midnight (e.g., 22:00 to 06:00).
 * 
 * @param {string} jobDate - The job date in YYYY-MM-DD format
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @param {string} nightStart - Night start time in HH:MM format (e.g., "22:00")
 * @param {string} nightEnd - Night end time in HH:MM format (e.g., "06:00")
 * @returns {number} - Night differential hours (rounded to 2 decimals)
 */
export function calculateNightDifferentialHours(
  jobDate,
  startTime,
  endTime,
  nightStart = "22:00",
  nightEnd = "06:00"
) {
  try {
    // Parse times to create Date objects for the job
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);

    const jobStartDate = new Date(jobDate);
    jobStartDate.setHours(startHour, startMin, 0, 0);

    const jobEndDate = new Date(jobDate);
    jobEndDate.setHours(endHour, endMin, 0, 0);

    // If end time is earlier than start time, it means job spans to next day
    if (jobEndDate <= jobStartDate) {
      jobEndDate.setDate(jobEndDate.getDate() + 1);
    }

    const [nightStartHour, nightStartMin] = nightStart.split(":").map(Number);
    const [nightEndHour, nightEndMin] = nightEnd.split(":").map(Number);

    const isCrossMidnight =
      nightEndHour < nightStartHour ||
      (nightEndHour === nightStartHour && nightEndMin < nightStartMin);

    const buildNightIntervalsForDate = (dateObj) => {
      const dateStr = dateObj.toISOString().split("T")[0];
      const intervals = [];

      if (isCrossMidnight) {
        // Night wraps midnight: 00:00–nightEnd and nightStart–24:00
        const earlyStart = new Date(`${dateStr}T00:00:00`);
        const earlyEnd = new Date(`${dateStr}T${nightEnd}:00`);
        intervals.push([earlyStart, earlyEnd]);

        const lateStart = new Date(`${dateStr}T${nightStart}:00`);
        const lateEnd = new Date(`${dateStr}T23:59:59.999`);
        intervals.push([lateStart, lateEnd]);
      } else {
        const nStart = new Date(`${dateStr}T${nightStart}:00`);
        const nEnd = new Date(`${dateStr}T${nightEnd}:00`);
        intervals.push([nStart, nEnd]);
      }

      return intervals;
    };

    let totalNightMinutes = 0;

    // Night intervals for the start date
    const startDayIntervals = buildNightIntervalsForDate(jobStartDate);

    // If job spills into the next calendar day, include that day's pattern too
    const endDayIntervals =
      jobEndDate.toISOString().split("T")[0] !==
      jobStartDate.toISOString().split("T")[0]
        ? buildNightIntervalsForDate(jobEndDate)
        : [];

    const allNightIntervals = [...startDayIntervals, ...endDayIntervals];

    for (const [nStart, nEnd] of allNightIntervals) {
      totalNightMinutes += calculateOverlapMinutes(
        jobStartDate,
        jobEndDate,
        nStart,
        nEnd
      );
    }

    return +(totalNightMinutes / 60).toFixed(2);
  } catch (error) {
    console.error("Error calculating night differential hours:", error);
    return 0;
  }
}


/**
 * Helper function to calculate overlap in minutes between two time periods.
 * 
 * @param {Date} start1 - Start of first period
 * @param {Date} end1 - End of first period
 * @param {Date} start2 - Start of second period
 * @param {Date} end2 - End of second period
 * @returns {number} - Overlap in minutes
 */
function calculateOverlapMinutes(start1, end1, start2, end2) {
  const overlapStart = Math.max(start1.getTime(), start2.getTime());
  const overlapEnd = Math.min(end1.getTime(), end2.getTime());
  
  if (overlapEnd > overlapStart) {
    return Math.round((overlapEnd - overlapStart) / 60000);
  }
  
  return 0;
}
